/**
 * @license Unit Formatter v1.2.0
 * https://github.com/rawify/UnitFormat.js
 *
 * Copyright (c) 2025, Robert Eisele (https://raw.org/)
 * Licensed under the MIT license.
 */

/** A supported scaling base. */
export type UnitBase = 2 | 10;

const PREFIX_EXPONENTS: Readonly<Record<UnitBase, Readonly<Record<string, number>>>> = {
  2: {
    E: 60,
    P: 50,
    T: 40,
    G: 30,
    M: 20,
    k: 10,
  },
  10: {
    E: 18,
    P: 15,
    T: 12,
    G: 9,
    M: 6,
    k: 3,
    h: 2,
    d: -1,
    c: -2,
    m: -3,
    u: -6,
    μ: -6,
    n: -9,
    p: -12,
    f: -15,
    a: -18,
  },
};

const NUMBER_FORMATTER = createNumberFormatter();

function createNumberFormatter(): Intl.NumberFormat | null {
  if (typeof Intl !== 'object' || typeof Intl.NumberFormat !== 'function') {
    return null;
  }

  try {
    return new Intl.NumberFormat(undefined, {
      maximumFractionDigits: 3,
      useGrouping: false,
    });
  } catch {
    return null;
  }
}

function isUnitBase(base: number): base is UnitBase {
  return base === 2 || base === 10;
}

function formatNumber(value: number): string {
  return NUMBER_FORMATTER?.format(value) ?? String(Math.round(value * 1000) / 1000);
}

/**
 * Formats a number with configurable decimal or binary engineering prefixes.
 *
 * @param value The number to format.
 * @param baseUnit The unit appended after the selected prefix.
 * @param prefixes Allowed single-character prefixes in priority order.
 * @param base Scaling base. Only 2 and 10 are supported.
 */
export function UnitFormat(
  value: number,
  baseUnit: string | null = '',
  prefixes: string | null = 'kMGTPE',
  base: number | null = 10,
): string {
  const resolvedUnit = baseUnit ?? '';
  const resolvedPrefixes = prefixes ?? 'kMGTPE';
  const resolvedBase = base ?? 10;

  if (!isUnitBase(resolvedBase)) {
    throw new Error(`Not supported base ${resolvedBase}`);
  }

  if (value === 0) {
    return `${formatNumber(0)}${resolvedUnit}`;
  }

  const magnitude = Math.abs(value);
  const exponents = PREFIX_EXPONENTS[resolvedBase];
  let bestPrefix = '';
  let bestScore = magnitude;

  for (const prefix of resolvedPrefixes) {
    const exponent = exponents[prefix];

    if (exponent === undefined) {
      throw new Error(`Unknown unit ${prefix}`);
    }

    const score = magnitude / Math.pow(resolvedBase, exponent);

    if (score >= 1 && (score < bestScore || bestScore < 1)) {
      bestScore = score;
      bestPrefix = prefix;
    }
  }

  const scaledValue = value < 0 ? -bestScore : bestScore;
  return `${formatNumber(scaledValue)}${bestPrefix}${resolvedUnit}`;
}

export default UnitFormat;